import { useCallback, useRef } from 'react';
import { useToggle } from 'react-use';
import cn from 'classnames';
import { ShiftSiblingData } from '@lp/features/Scheduler/types';
import { ShiftButton, UnblockShiftPopover } from '@lp/features/Scheduler/components';
import { CSShiftCellDropdown } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/components';
import {
  getCSShiftColors,
  getCSShiftEdgeColors,
  getCSShiftEdgeVariant,
  getCSShiftTypePrimaryText,
  getCSShiftTypeSecondaryText,
  getCSShowHrIcon,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/utils';
import { singleCSShiftTypes } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/constants';
import { CSResource, CSShift, CSShiftSelectPayload, ShiftFixationType } from '@/api/apis/resources/schedule';
import { CSShiftEdgePatch } from './CSShiftEdgePatch';

type CSShiftCellProps = {
  resource?: CSResource;
  shiftData: CSShift;
  prevShift?: CSShift;
  prevIsExternal?: boolean;
  siblingData?: ShiftSiblingData;
  onShiftChange: (shiftPayload?: CSShiftSelectPayload) => void;
  lighten?: boolean;
  prevLighten?: boolean;
  readonly?: boolean;
  canReset?: boolean;
  canPhoneSupport?: boolean;
  canUnblock?: boolean;
  usesDeductionRules?: boolean;
  onUnblock?: () => void;
};

export const CSShiftCell = ({
  resource,
  shiftData,
  prevShift,
  prevIsExternal,
  siblingData,
  onShiftChange,
  lighten,
  prevLighten,
  readonly,
  canReset,
  canUnblock,
  usesDeductionRules,
  onUnblock,
}: CSShiftCellProps) => {
  const elRef = useRef<HTMLButtonElement>(null);
  const [isOpen, toggleIsOpen] = useToggle(false);
  const { shiftType, isEditable, fixationType, timeGroup } = shiftData;
  const { hasPrev, isExternal, prevEdge } = siblingData ?? {};
  const isCentered = isExternal || singleCSShiftTypes.includes(shiftType);
  const isBlockedRequired = !isEditable && fixationType === ShiftFixationType.Required;
  const disabled = !isEditable || readonly;
  const showContent = !hasPrev;
  const showTimeGroup = !!usesDeductionRules && !isExternal && !!timeGroup;
  const primaryText = siblingData?.isExternal ? siblingData.name : getCSShiftTypePrimaryText(shiftData, showTimeGroup);
  const secondaryText = siblingData?.isExternal ? null : getCSShiftTypeSecondaryText(shiftData);
  const colors = getCSShiftColors(shiftData, isExternal);

  const canShowUnblock = isBlockedRequired && canUnblock && !readonly;

  const onClick = useCallback(() => {
    toggleIsOpen();
  }, [toggleIsOpen]);

  return (
    <>
      <div className="relative size-full">
        {prevEdge && (
          <CSShiftEdgePatch
            kind={prevEdge}
            variant={getCSShiftEdgeVariant(shiftData)}
            prev={getCSShiftEdgeColors(prevShift, prevIsExternal)}
            current={getCSShiftEdgeColors(shiftData, isExternal)}
            prevLighten={prevLighten}
            currentLighten={lighten}
          />
        )}
        <ShiftButton
          ref={elRef}
          lighten={lighten}
          disabled={disabled && !canShowUnblock}
          isCentered={isCentered}
          siblingData={siblingData}
          className={cn(colors.bg, colors.text, colors.border)}
          style={colors.style}
          onClick={onClick}
          showHrIcon={getCSShowHrIcon(shiftData)}
        >
          {showContent && (
            <div className="flex flex-col items-start z-1">
              <div className="flex flex-wrap items-center text-start gap-x-1">{primaryText}</div>
              {secondaryText && (
                <span className={cn('text-xs-r mt-0.5 text-start', colors.secondaryText)}>{secondaryText}</span>
              )}
            </div>
          )}
        </ShiftButton>
      </div>

      {isOpen && canShowUnblock && onUnblock && (
        <UnblockShiftPopover triggerRef={elRef} isOpen={isOpen} onOpenChange={toggleIsOpen} onUnblock={onUnblock} />
      )}

      {isOpen && !canShowUnblock && (
        <CSShiftCellDropdown
          triggerRef={elRef}
          isOpen={isOpen}
          onOpenChange={toggleIsOpen}
          shiftData={shiftData}
          onShiftChange={onShiftChange}
          isExternalHub={isExternal}
          canReset={canReset}
          allowedOrgans={resource?.organs}
          canPhoneSupport={resource?.canPhoneSupport}
          resourceTimeGroup={resource?.shiftTimeGroup}
          showTimeGroup={usesDeductionRules}
        />
      )}
    </>
  );
};
